import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import swal from 'sweetalert'

import { CatchError } from 'Exceptions/exceptions'
import { currencyFormat } from 'Main/Utils'
import { isDokuPaymentVendor } from 'Main/paymentVendorTypes'
import PaymentVendorsService from 'Main/service/PaymentVendorsService'
import CommonListApiPaginated from 'views/components/Table/CommonListApiPaginated'
import { getRefreshToken } from 'variables/common'

function PaymentVendorsManager () {
  const navigate = useNavigate()
  const refreshToken = getRefreshToken()
  const paymentVendorsService = useMemo(() => new PaymentVendorsService(), [])
  const [reloadKey, setReloadKey] = useState(0)
  const [vendors, setVendors] = useState([])
  const [testingId, setTestingId] = useState(null)
  const [checkingId, setCheckingId] = useState(null)

  const getVendors = useCallback(() => paymentVendorsService.getAll(), [paymentVendorsService])

  const handleListError = useCallback((error) => {
    CatchError(error, navigate, refreshToken)
  }, [navigate, refreshToken])

  const handleData = useCallback((response, result) => {
    setVendors(result.rows)
  }, [])

  const handleTest = useCallback(async (id, name) => {
    setTestingId(id)
    try {
      const result = await paymentVendorsService.testConnection(id)
      await swal('Connected!', `${name}: ${result.message || 'Success'}`, 'success')
    } catch (error) {
      await swal('Failed!', `${name}: ${error?.data?.message || error?.message}`, 'warning')
    } finally {
      setTestingId(null)
    }
  }, [paymentVendorsService])

  const handleCheckBalance = useCallback(async (id, name) => {
    setCheckingId(id)
    try {
      const result = await paymentVendorsService.checkBalance(id)
      await swal('Success', `${name}: ${result.message || 'Balances retrieved successfully'}`, 'success')
      setReloadKey((current) => current + 1)
    } catch (error) {
      await swal('Failed!', `${name}: ${error.message}`, 'warning')
    } finally {
      setCheckingId(null)
    }
  }, [paymentVendorsService])

  const handleEdit = useCallback((id) => {
    navigate(`/dashboard/payment-vendors/${id}`)
  }, [navigate])

  const handleDelete = useCallback((id, name) => {
    swal('Delete Vendor Gateway', `Are You Sure Delete ${name}?`, 'warning', {
      buttons: {
        cancel: 'Cancel',
        confirm: { text: 'Delete', value: 'delete' }
      }
    }).then(async (value) => {
      if (value !== 'delete') return

      try {
        await paymentVendorsService.remove(id)
        await swal('Success!', 'Vendor Gateway Deleted', 'success')
        setReloadKey((current) => current + 1)
      } catch (error) {
        await swal('Error', error.message, 'error')
      }
    })
  }, [paymentVendorsService])

  const showBalanceColumn = vendors.some((vendor) => (
    isDokuPaymentVendor(vendor.enviroment_type) && vendor.is_payment_support
  ))

  const columns = useMemo(() => [
    {
      header: 'ID',
      accessorKey: 'id',
      maxWidth: 60
    },
    {
      header: 'Name',
      accessorKey: 'name',
      maxWidth: 150,
    },
    {
      header: 'Client ID',
      accessorKey: 'client_id',
      maxWidth: 280,
    },
    ...(showBalanceColumn
      ? [{
          header: 'Saldo',
          accessorKey: 'krmdoku_aval_balance',
          maxWidth: 130,
          cell: ({ row, getValue }) => isDokuPaymentVendor(row.original.enviroment_type) && row.original.is_payment_support
            ? currencyFormat(getValue())
            : '-'
        }]
      : []),
    {
      header: 'Environment',
      accessorKey: 'enviroment_type',
      maxWidth: 100,
    },
    {
      header: 'Production',
      accessorKey: 'is_production',
      maxWidth: 140,
      cell: ({ getValue }) => getValue()
        ? <span className="status-primary">Production</span>
        : <span className="status-secondary">Sandbox</span>
    },
    {
      header: 'Enabled',
      accessorKey: 'enabled',
      maxWidth: 100,
      cell: ({ getValue }) => getValue()
        ? <span className="status-success">Enabled</span>
        : <span className="status-danger">Disabled</span>
    },
    {
      header: 'VA Type',
      accessorKey: 'is_fixed_bill',
      maxWidth: 100,
      cell: ({ getValue }) => getValue()
        ? <span className="status-success">Fixed Bill</span>
        : <span className="status-danger">Float Bill</span>
    },
    {
      header: 'Type',
      accessorKey: 'connection_type',
      maxWidth: 80,
    },
    {
      header: 'Action',
      id: 'actions',
      enableSorting: false,
      filterable: false,
      exportable: false,
      cell: ({ row }) => {
        const vendor = row.original
        return (
          <div className="d-flex flex-wrap gap-1">
            <button
              type="button"
              className="btn btn-warning btn-sm"
              disabled={testingId === vendor.id}
              title="Test Connection"
              onClick={() => handleTest(vendor.id, vendor.name)}>
              <i className={`fa-solid ${testingId === vendor.id ? 'fa-circle-o-notch fa-spin' : 'fa-wifi'}`} />
            </button>
            <button
              type="button"
              className="btn btn-info btn-sm"
              disabled={(checkingId === vendor.id) && (!vendor.is_payment_support)}
              title="Cek Saldo"
              onClick={() => handleCheckBalance(vendor.id, vendor.name)}>
              <i className={`fa-solid ${checkingId === vendor.id ? 'fa-circle-o-notch fa-spin' : 'fa-wallet'}`} />
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              title="Edit Vendor Gateway"
              onClick={() => handleEdit(vendor.id)}>
              <i className="fa-solid fa-pen-to-square" />
            </button>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              title="Delete Vendor Gateway"
              onClick={() => handleDelete(vendor.id, vendor.name)}>
              <i className="fa-solid fa-trash-can" />
            </button>
          </div>
        )
      }
    }
  ], [checkingId, handleCheckBalance, handleDelete, handleEdit, handleTest, showBalanceColumn, testingId])

  const menu = (
    <div className="text-center">
      <button type="button" className="btn btn-success" title="Tambah Vendor Gateway" onClick={() => navigate('/dashboard/payment-vendors/add')}>
        <i className="fa-solid fa-pen-to-square" />&nbsp;&nbsp; Tambah
      </button>
    </div>
  )

  return (
    <>

      <div className="page-content">
        <section>
          <CommonListApiPaginated
            header="List Vendor Gateway"
            subtitle=""
            columns={columns}
            getData={getVendors}
            menu={menu}
            reloadKey={reloadKey}
            loadingLabel="Loading Vendor Gateway..."
            emptyLabel="No Vendor Gateway found."
            onData={handleData}
            onError={handleListError}
          />
        </section>
      </div>
    </>
  )
}

export default PaymentVendorsManager
